import { Close } from "../../../assets/icons";
import React, { useEffect, useRef } from "react";

interface DrawerProps {
    title?: string;
    width?: string;
    isOpen: boolean;
    onClose: () => void;
    onConfirm?: () => void;
    children: React.ReactNode;
    showButton?: boolean;
    disabled?: boolean;
    bgColor?: string;
    className?: string;
    confirmLabel?: string;
}

const Drawer: React.FC<DrawerProps> = ({
    width,
    title,
    isOpen,
    onClose,
    onConfirm,
    children,
    showButton = true,
    disabled,
    bgColor,
    className,
    confirmLabel = "Confirm",
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add("overflow-hidden");
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.body.classList.remove("overflow-hidden");
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.body.classList.remove("overflow-hidden");
            document.removeEventListener("mousedown", handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleClickOutside = (event: MouseEvent) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
        ) {
            onClose();
        }
    };

    return (
        <div
            className={`fixed inset-0 transition-transform z-[10010] ${isOpen ? "transform translate-x-0" : "transform translate-x-full"
                }`}
            ref={dropdownRef}
        >
            <div className="absolute inset-0">
                <div
                    className="absolute inset-0 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                >
                    <div className="absolute inset-0 opacity-75" />
                </div>

                <div
                    className={`fixed inset-y-0 right-0 pl-16 xl:pl-10 max-w-full flex ${width}`}
                >
                    <div
                        className={`relative w-screen ${bgColor ? bgColor : "bg-darkcolor-700"
                            } overflow-auto border-l-[1px] border-l-darkcolor-400`}
                    >
                        <div className="h-full flex flex-col shadow-xl">
                            <div className="flex justify-between items-center px-[24px] sm:px-6 border-b-[1px] py-[20px] border-b-darkcolor-400">
                                <div className="font-bold text-neutral-25 text-[23px]">
                                    {title}
                                </div>
                                <Close onClick={onClose} className="cursor-pointer" />
                            </div>
                            <div
                                className={`flex-1 ${className ? className : "overflow-auto"}`}
                            >
                                {children}
                            </div>
                            {showButton && (
                                <div
                                    className={`sticky bottom-0 ${bgColor ? bgColor : "bg-darkcolor-700"
                                        } border-t-[1px] border-t-darkcolor-400 px-6 py-4 flex justify-end space-x-4 z-[10011]`}
                                >
                                    <button
                                        onClick={onClose}
                                        className="flex items-center justify-center py-2 px-4 gap-2 rounded-[8px] border-[1px] border-darkcolor-400 text-sm font-normal text-neutral-25 bg-darkcolor-600 hover:bg-darkcolor-500 hover:border-darkcolor-300 transition"
                                    >
                                        Cancel
                                    </button>
                                    {confirmLabel && (
                                        <button
                                            onClick={onConfirm}
                                            disabled={disabled}
                                            className={`flex items-center justify-center py-2 px-4 gap-2 rounded-[8px] border-[1px] text-sm font-bold shadow-custom ${disabled
                                                    ? "border-[1px] bg-darkcolor-700 border-darkcolor-700 text-darkcolor-300 cursor-not-allowed"
                                                    : "text-white border-rgba(255, 255, 255, 0.12) bg-primary-500 hover:bg-primary-600 active:text-[#FCFCFD]"
                                                }`}
                                        >
                                            {confirmLabel}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(Drawer);
